import { valibotResolver } from "@hookform/resolvers/valibot";
import { PlusCircle } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAudienceMutations from "../../../../datahooks/useAudienceMutations";
import { ProfileUrlSchema } from "../../../../schemas";

export default function AddTarget() {
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useTwBreakpoint("lg");

  const { addTargetToCampaign, isAddingTargetToCampaign } =
    useAudienceMutations();

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      url: "",
    },
    resolver: valibotResolver(ProfileUrlSchema),
  });

  function onSubmit(data: { url: string }) {
    addTargetToCampaign({ profileUrl: data.url })
      .then(() => {
        setIsOpen(false);
        reset();
      })
      .catch((err) => setError("url", { message: err.serverMessage }));
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen} onAfterClose={reset}>
      <Trigger asChild>
        <Button
          leftIcon={<PlusCircle weight="fill" />}
          variant="outline-black"
          className="only:flex-1 lg:ml-auto"
          size={isDesktop ? "sm" : "md"}
        >
          Add target
        </Button>
      </Trigger>

      <Content>
        <Header title="Add new target to a campaign" />

        <Body>
          <FormField
            label="Profile URL"
            htmlFor="profile_url"
            size="lg"
            error={errors.url?.message}
          >
            <Input
              id="profile_url"
              placeholder="Enter a LinkedIn URL here"
              variant="lg"
              {...register("url")}
            />
          </FormField>

          <Alert variant="warning" className="mt-3">
            When adding targets to a list campaign with custom variables, please
            make sure your templates have fallback values for each variable.
          </Alert>
        </Body>

        <Footer className="justify-end">
          <Close asChild>
            <Button
              variant="secondary-black"
              onClick={() => setIsOpen(false)}
              disabled={isAddingTargetToCampaign}
            >
              Cancel
            </Button>
          </Close>
          <Button
            onClick={handleSubmit(onSubmit)}
            isLoading={isAddingTargetToCampaign}
          >
            Add target
          </Button>
        </Footer>
      </Content>
    </Component>
  );
}
